import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import LandingArticle from '@/components/LandingArticle';

const InlineCTA = () => (
  <div className="my-6 not-prose rounded-2xl border border-emerald-700/15 bg-emerald-50/60 p-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between" data-testid="inline-cta">
    <div className="text-sm text-emerald-900">
      <strong>Looking for a high-bioavailability curcumin formula?</strong>
      <span className="block text-emerald-800/80 mt-0.5">BCM-95® — the patented Curcugreen<sup>&reg;</sup> extract. £37.99 first-bottle welcome offer.</span>
    </div>
    <Link to="/products/94056720-f372-4eef-bcfe-74063137a6e5" className="inline-flex shrink-0 items-center gap-1.5 bg-emerald-700 text-white font-bold py-2.5 px-4 rounded-full text-sm hover:bg-emerald-800 transition-colors">
      Shop now <ArrowRight className="w-3.5 h-3.5" />
    </Link>
  </div>
);

const sections = [
  {
    id: 'short-answer',
    heading: 'The short answer',
    body: (
      <>
        <p><strong>Turmeric</strong> is the bright-orange rhizome (root) of <em>Curcuma longa</em>, traditionally used as a culinary spice. <strong>Curcumin</strong> is the principal bioactive compound found inside turmeric — but it makes up only around <strong>2–5%</strong> of the dry root by weight.</p>
        <p>In other words: every curcumin molecule comes from turmeric, but not every gram of turmeric gives you a meaningful curcumin dose. A teaspoon of culinary turmeric (~3 g) contains roughly 60–150 mg of curcumin. A standardised curcumin food supplement like BCM-95® delivers <strong>650 mg of curcumin per 2 capsules</strong> — typically 10–20× the dose you would get from cooking.</p>
        <p className="text-sm italic text-slate-500">Source: Tayyem RF et al., Curcumin content of turmeric and curry powders, Nutr Cancer 2006.</p>
      </>
    ),
  },
  {
    id: 'composition',
    heading: 'What is actually inside the turmeric root?',
    body: (
      <>
        <p>Turmeric contains three main families of bioactive compounds:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li><strong>Curcuminoids (~2–9%)</strong> — curcumin, demethoxycurcumin (DMC), and bisdemethoxycurcumin (BDMC). Curcumin is the most studied.</li>
          <li><strong>Essential oils / turmerones (~3–7%)</strong> — ar-turmerone, alpha- and beta-turmerone. Modern research suggests these volatile oils may help <em>enhance the absorption</em> of curcumin from the gut.</li>
          <li><strong>Polysaccharides, fibre, starches</strong> — the bulk of the root by weight, but inert from a supplement standpoint.</li>
        </ul>
        <p>Plain turmeric powder gives you everything in roughly natural proportions. A standardised curcumin extract concentrates the curcuminoid fraction many-fold so a daily dose is practical in two capsules.</p>
      </>
    ),
  },
  {
    id: 'bioavailability',
    heading: 'Why bioavailability changes everything',
    body: (
      <>
        <p>Curcumin in its raw, isolated form is notoriously poorly absorbed by the human gut. It is poorly water-soluble, rapidly metabolised by the liver, and quickly excreted. Numerous early pharmacokinetic studies measured almost-undetectable curcumin plasma levels after standard turmeric doses.</p>
        <p>To get a curcumin food supplement that the body can actually use, manufacturers have explored several strategies:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li><strong>Black pepper / piperine</strong> — the original bioavailability hack, but piperine can interact with prescription medicines metabolised by CYP3A4.</li>
          <li><strong>Phospholipid / lecithin complexes</strong> — wraps curcumin in a lipid carrier (e.g. Meriva).</li>
          <li><strong>Nanoparticles / micelles</strong> — ultra-fine emulsions.</li>
          <li><strong>Curcumin + turmeric essential oils</strong> — the approach used by BCM-95® / Curcugreen<sup>&reg;</sup>, the patented formulation behind BCM-95®.</li>
        </ul>
        <p>The BCM-95® approach is notable because it uses <em>only ingredients from the turmeric plant itself</em> — no piperine, no synthetic carriers — which keeps the supplement clean-label and reduces drug-interaction concerns.</p>
        <InlineCTA />
      </>
    ),
  },
  {
    id: 'cooking',
    heading: 'Cooking with turmeric vs taking a curcumin supplement',
    body: (
      <>
        <p>The two are not mutually exclusive. Adding turmeric to curries, golden lattes or rice carries a long, safe culinary tradition and contributes flavour, colour and trace amounts of curcumin. If you also fry the turmeric briefly in oil with black pepper, you slightly improve curcumin extraction and absorption.</p>
        <p>However, if your goal is a <strong>standardised, repeatable daily dose</strong> of curcumin — for example, taking it as part of an active-living routine — a clinically-studied curcumin food supplement is far more practical than trying to eat enough turmeric.</p>
      </>
    ),
  },
  {
    id: 'who-should',
    heading: 'Who typically takes a curcumin food supplement?',
    body: (
      <>
        <p>In the UK, curcumin supplements are popular among:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>Active adults &amp; recreational athletes interested in <strong>joint comfort and exercise recovery</strong> support.</li>
          <li>Adults over 45 maintaining everyday mobility and an active lifestyle.</li>
          <li>People exploring plant-based, polyphenol-rich routines alongside vitamin D, omega-3 and magnesium.</li>
        </ul>
        <p className="text-[13px] italic text-slate-500">Food supplements should not be used as a substitute for a varied and balanced diet and a healthy lifestyle. Not intended to diagnose, treat, cure or prevent any disease.</p>
      </>
    ),
  },
  {
    id: 'choosing',
    heading: 'How to choose a UK curcumin supplement',
    body: (
      <>
        <ol className="list-decimal pl-6 space-y-2">
          <li><strong>Look for the actual curcumin dose</strong> — not just "turmeric 500 mg". A respectable supplement states the curcumin content per serving (e.g. 650 mg).</li>
          <li><strong>Check for a patented or clinically-studied extract</strong> — BCM-95<sup>&reg;</sup> / Curcugreen<sup>&reg;</sup>, Meriva, Theracurmin, Longvida. These have published pharmacokinetic data.</li>
          <li><strong>Bioavailability strategy</strong> — make sure the product explains <em>how</em> absorption is enhanced (turmeric oils, phospholipids, micelles, etc.).</li>
          <li><strong>Clean-label &amp; allergen-free</strong> — vegan, no unnecessary excipients, no artificial colours.</li>
          <li><strong>GMP-certified manufacture</strong> — proof of consistent quality control batch-to-batch.</li>
          <li><strong>UK consumer protections</strong> — buy from a registered UK food business with clear returns, VAT receipt, and a real address.</li>
        </ol>
      </>
    ),
  },
  {
    id: 'faq',
    heading: 'Frequently asked questions',
    body: (
      <>
        <p className="font-semibold text-emerald-900">Is curcumin the same as turmeric?</p>
        <p>No. Turmeric is the whole root; curcumin is one bioactive compound inside it (~2–5% by weight). Most "turmeric capsules" sold without a curcumin specification deliver a small fraction of a standardised curcumin dose.</p>
        <p className="font-semibold text-emerald-900">Do I need black pepper or piperine?</p>
        <p>Not with BCM-95® / Curcugreen<sup>&reg;</sup>. Bioavailability is enhanced using the turmeric plant's own essential oils, which avoids the piperine route entirely.</p>
        <p className="font-semibold text-emerald-900">How long until I notice anything?</p>
        <p>Most curcumin trials measure outcomes over 8–12 weeks of consistent daily use. Like most polyphenol-based food supplements, consistency matters more than dose.</p>
        <p className="font-semibold text-emerald-900">Is it safe with medications?</p>
        <p>Curcumin is generally well tolerated. If you take blood thinners, gallbladder medication, or anything metabolised by CYP3A4, speak to your GP or pharmacist before starting any new food supplement.</p>
      </>
    ),
  },
];

export default function CurcuminVsTurmeric() {
  return (
    <LandingArticle
      title="Curcumin vs Turmeric: What's the Difference? | IIY Phytoventra"
      description="Curcumin and turmeric are not the same. A UK-friendly guide to the difference, bioavailability, dosing, and how to choose a clinically-studied curcumin food supplement."
      pathOverride="/curcumin-vs-turmeric"
      heading="Curcumin vs Turmeric: What's the Real Difference?"
      subheading="Turmeric is the spice. Curcumin is the bioactive compound inside it — and there is a big difference between cooking with one and supplementing with the other. A plain-English UK guide."
      tag="Curcumin Guide"
      datePublished="2026-02-10"
      sections={sections}
    />
  );
}
